// Shared, `confirm()`-free confirmation dialog.
//
// Why this exists
// ---------------
// iOS Safari silently ignores `window.confirm()` when it is called without
// "user activation". Turbo's `FormSubmission.start()` is `async` and calls the
// configured confirm method *after* an `await`:
//
//   const answer = await confirmMethod(confirmationMessage, ...)
//
// After a Turbo visit (fetch + DOM swap) Safari has already dropped the
// activation, so `confirm()` returns `false` without ever showing a dialog.
// That breaks every `data-turbo-confirm` delete link on iOS (reload still
// works because the activation is fresh) - hotwired/turbo#637.
//
// A DOM-based dialog does not depend on user activation, so it works in that
// situation too. It also preserves the newlines that the app puts in its
// confirmation messages.
//
// `confirmDialog(message)` returns a `Promise<boolean>` that resolves to
// `true` for OK and `false` for cancel / Esc / backdrop click.

const STYLE_ID = 'kucc-confirm-style'

// Deliberately plain DOM + a small, self-contained stylesheet. The markup uses
// Bootstrap 4's `.btn` classes so it blends into the app, but it does not use
// Bootstrap's modal JS (no jQuery dependency, no z-index/backdrop surprises,
// and it works on pages where only the Turbo entrypoint is loaded).
const CSS = `
.kucc-confirm-overlay {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 20000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgba(0, 0, 0, 0.5);
  -webkit-tap-highlight-color: transparent;
}
.kucc-confirm-overlay.is-entering { opacity: 0; }
.kucc-confirm-overlay.is-open { opacity: 1; transition: opacity 0.15s ease-out; }
.kucc-confirm-dialog {
  width: 100%;
  max-width: 420px;
  background: #fff;
  border-radius: 0.3rem;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.25);
  outline: none;
}
.kucc-confirm-dialog.is-entering { transform: translateY(-0.5rem); }
.kucc-confirm-dialog.is-open { transform: none; transition: transform 0.15s ease-out; }
.kucc-confirm-message {
  margin: 0;
  padding: 1.25rem;
  color: #212529;
  font-size: 1rem;
  line-height: 1.5;
  white-space: pre-line;
  word-break: break-word;
}
.kucc-confirm-actions {
  display: flex;
  justify-content: flex-end;
  padding: 0.75rem 1.25rem;
  border-top: 1px solid #dee2e6;
}
.kucc-confirm-actions .btn { min-width: 5rem; }
.kucc-confirm-actions .btn + .btn { margin-left: 0.5rem; }
@media (prefers-reduced-motion: reduce) {
  .kucc-confirm-overlay.is-open,
  .kucc-confirm-dialog.is-open { transition: none; }
}
`

function ensureStyles() {
  if (document.getElementById(STYLE_ID)) return
  const style = document.createElement('style')
  style.id = STYLE_ID
  style.textContent = CSS
  document.head.appendChild(style)
}

let activeDialog = null

/**
 * Show a modal confirmation dialog.
 *
 * @param {string} message Message to display. Newlines are preserved.
 * @param {{ okLabel?: string, cancelLabel?: string }} [options]
 * @returns {Promise<boolean>} true for OK, false for cancel/Esc/backdrop.
 */
export function confirmDialog(message, options = {}) {
  // If a Turbo visit swapped the body while a dialog was open, the overlay is
  // gone but the module-level guard would otherwise stay set forever.
  if (activeDialog && activeDialog.overlay && !document.contains(activeDialog.overlay)) {
    activeDialog = null
  }
  // Ignore re-entrant calls while a dialog is already open. Returning `false`
  // for the duplicate (rather than resolving with the first answer) makes sure
  // a double click cannot run the destructive action twice.
  if (activeDialog) return Promise.resolve(false)

  ensureStyles()

  const okLabel = options.okLabel || 'OK'
  const cancelLabel = options.cancelLabel || 'キャンセル'

  return new Promise((resolve) => {
    const previouslyFocused = document.activeElement
    const previousOverflow = document.body.style.overflow

    const overlay = document.createElement('div')
    overlay.className = 'kucc-confirm-overlay is-entering'

    const dialog = document.createElement('div')
    dialog.className = 'kucc-confirm-dialog is-entering'
    dialog.setAttribute('role', 'dialog')
    dialog.setAttribute('aria-modal', 'true')
    dialog.tabIndex = -1

    const messageId = 'kucc-confirm-message-' + Date.now().toString(36)
    const messageEl = document.createElement('p')
    messageEl.id = messageId
    messageEl.className = 'kucc-confirm-message'
    // textContent, never innerHTML: messages embed user content such as an
    // article title.
    messageEl.textContent = message
    dialog.setAttribute('aria-describedby', messageId)

    const actions = document.createElement('div')
    actions.className = 'kucc-confirm-actions'

    const cancelButton = document.createElement('button')
    cancelButton.type = 'button'
    cancelButton.className = 'btn btn-secondary kucc-confirm-cancel'
    cancelButton.textContent = cancelLabel

    const okButton = document.createElement('button')
    okButton.type = 'button'
    okButton.className = 'btn btn-primary kucc-confirm-ok'
    okButton.textContent = okLabel

    actions.appendChild(cancelButton)
    actions.appendChild(okButton)
    dialog.appendChild(messageEl)
    dialog.appendChild(actions)
    overlay.appendChild(dialog)

    let settled = false

    const cleanup = () => {
      document.removeEventListener('keydown', onKeydown, true)
      if (overlay.parentNode) overlay.parentNode.removeChild(overlay)
      document.body.style.overflow = previousOverflow
      activeDialog = null
      if (
        previouslyFocused &&
        typeof previouslyFocused.focus === 'function' &&
        document.contains(previouslyFocused)
      ) {
        try {
          previouslyFocused.focus({ preventScroll: true })
        } catch (e) {
          previouslyFocused.focus()
        }
      }
    }

    const settle = (answer) => {
      if (settled) return
      settled = true
      resolve(answer)
      cleanup()
    }

    const onKeydown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        settle(false)
      } else if (event.key === 'Enter') {
        event.preventDefault()
        settle(true)
      } else if (event.key === 'Tab') {
        // Keep focus inside the two buttons while the dialog is open.
        const focusables = [cancelButton, okButton]
        const index = focusables.indexOf(document.activeElement)
        const next = event.shiftKey
          ? index <= 0
            ? focusables.length - 1
            : index - 1
          : index === focusables.length - 1
            ? 0
            : index + 1
        event.preventDefault()
        focusables[next].focus()
      }
    }

    okButton.addEventListener('click', () => settle(true))
    cancelButton.addEventListener('click', () => settle(false))
    overlay.addEventListener('mousedown', (event) => {
      if (event.target === overlay) settle(false)
    })

    document.body.style.overflow = 'hidden'
    document.body.appendChild(overlay)
    document.addEventListener('keydown', onKeydown, true)

    activeDialog = { settle, overlay }

    // Transition in (skipped by CSS when prefers-reduced-motion: reduce).
    const reveal = () => {
      overlay.classList.remove('is-entering')
      dialog.classList.remove('is-entering')
      overlay.classList.add('is-open')
      dialog.classList.add('is-open')
      try {
        okButton.focus({ preventScroll: true })
      } catch (e) {
        okButton.focus()
      }
    }

    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(reveal)
    } else {
      reveal()
    }
  })
}

export default confirmDialog
